import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import {
  artifactDownloadPath,
  artifactExportPath,
  type ArtifactDownloadResult,
  type ArtifactExportResult,
  type ConvertAndCopyFormat,
} from "~/services/artifacts";
import { apiMutate } from "~/utils/apiClient";

export type DownloadResult = { ok: true } | { ok: false; error: string };

export type ExportResult =
  | { ok: true; result: ArtifactExportResult }
  | { ok: false; error: string };

// Per-artifact mutating actions: download (fetch presigned URL + trigger
// browser save) and export-to-workbench (drop a converted copy into a
// workbench project's exports directory). Stateless — each call returns a
// result object so callers can manage their own stages and error UI.
//
// The workbench project id is supplied per call (selected in the export
// dialog) because RWB workbench projects are an independent identifier
// space from AI projects — the conversation has no implicit workbench.
export function useArtifactActions(artifactId: string) {
  const { conversationId } = useConversationContext();

  const download = async (format: ConvertAndCopyFormat): Promise<DownloadResult> => {
    try {
      const result = await apiMutate<ArtifactDownloadResult>(
        artifactDownloadPath(conversationId, artifactId),
        "POST",
        { format }
      );
      // Pull the bytes ourselves so the browser uses our filename rather
      // than whatever the presigned URL hands back via Content-Disposition.
      const res = await fetch(result.download_url);
      if (!res.ok) throw new Error(`Download failed with status ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = result.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  };

  const exportToWorkbench = async (
    format: ConvertAndCopyFormat,
    workbenchProjectId: number
  ): Promise<ExportResult> => {
    try {
      const result = await apiMutate<ArtifactExportResult>(
        artifactExportPath(conversationId, artifactId),
        "POST",
        { project_id: workbenchProjectId, format }
      );
      return { ok: true, result };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
  };

  return {
    download,
    exportToWorkbench,
  };
}
